import http from "@http";
import config from "@config";
import { sortToString } from "@transformers/sort";
import { transformFilters, OPERATORS } from "@transformers/request";
import actionCreators from "@modules/employee/actionCreator";
import employmentDetailsSaga from "./employment-details";
import personalDetailsSaga from "./personal-details";
import medicalDetailsSaga from "./medical-details";
import userAccountSaga from "./user-account-details";
import {
  GetActiveClients,
  GetAllActiveClientsInformation,
  GetEmployee,
  ExportEmployeeReferralHistory,
  ExportEmployeeReferralHistorySuccess,
  GetEmployeeReferralHistory,
  GetEmployeeActionSummary,
  SearchEmployeeByName,
  GetEmployees,
  GetEmployeesSecurityQuestions,
  SetEmployeesSort,
  GetEmployeeSchema,
  GetAddEmployeeSchema,
  GetEmployeeTempSchema,
  GetReportLogData,
  ExportEmployeeDeclinedReferralHistory,
  ExportEmployeeDeclinedReferralHistorySuccess,
  SendGSAppInvitation,
} from "@modules/employee/constants/actions";
import { all, call, put, takeEvery, takeLatest, select, spawn } from "redux-saga/effects";
import {
  getChangelogSort,
  getEmployeeFilters,
  getEmployeeNameSearchform,
  getEmployeesSort,
} from "@modules/employee/selectors";
import { downloadCsvFile } from "@sagas/utils";
import recordingActionCreator from "@modules/recording/actionCreator";
import { parseSchema } from "@utils/schema";
import { sendSuccessMessage } from "@modules/app/actionCreator";

export function* onGetEmployeeReferralHistory({ payload }) {
  const { clientId, employeeId } = payload;

  if (!clientId || !employeeId) {
    throw Error("Invalid payload provided");
  }
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/reports/client/${clientId}/employee/${employeeId}/referrals`,
    );

    yield put(actionCreators.GetEmployeeReferralHistorySuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetEmployeeReferralHistoryError(e.response));
  }
}

export function* onExportEmployeeReferralHistory({ payload }) {
  const { clientId, employeeId } = payload;

  if (!clientId || !employeeId) {
    throw Error("Invalid payload provided");
  }
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/reports/client/${clientId}/employee/${employeeId}/referrals/export`,
    );

    yield put(actionCreators.ExportEmployeeReferralHistorySuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.ExportEmployeeReferralHistoryError(e.response));
  }
}

export function* onExportEmployeeReferralHistorySuccess({ payload }) {
  yield downloadCsvFile(payload, "WellbeingReferrals.csv");
}

export function* onGetEmployeeSchema({ payload }) {
  const clientId = payload.clientId;
  try {
    const schema = yield parseSchema("EmployeeDTO", { clientId });

    yield put(actionCreators.getEmployeeSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getEmployeeSchemaError(e.response));
  }
}

export function* onGetEmployeeTempSchema({ payload }) {
  const clientId = payload.clientId;
  try {
    const schema = yield parseSchema("TemporaryEmployeeDTO", { clientId });
    yield put(actionCreators.getEmployeeSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getEmployeeSchemaError(e.response));
  }
}

export function* onGetAddEmployeeSchema({ payload }) {
  try {
    const schema = yield parseSchema("EmployeeDTO", { clientId: payload.clientId });

    yield put(actionCreators.getAddEmployeeSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getAddEmployeeSchemaError(e.response));
  }
}

export function* onGetEmployee({ payload }) {
  const clientId = payload.clientId;
  const employeeId = payload.employeeId;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee/${employeeId}`,
    );
    yield put(actionCreators.getEmployeeSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getEmployeeError(e.response));
  }
}

export function* onGetEmployeeActionSummary({ payload }) {
  const clientId = payload.clientId;
  const employeeId = payload.employeeId;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee/${employeeId}/summary`,
    );
    yield put(actionCreators.getEmployeeActionSummarySuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getEmployeeActionSummaryError(e.response));
  }
}

export function* onGetActiveClients({ payload }) {
  const limit = payload.limit || 100;
  const { name, is_operating_company } = payload;
  const status = payload.status || "ACTIVE";

  const search = transformFilters(Object.assign({}, { name, status, is_operating_company }), {
    name: OPERATORS.LIKE,
  });

  const params = {
    search,
    limit,
  };
  try {
    const response = yield call(http.get, `${config.endpointBaseUrl}api/v1/client-admin/client`, {
      params,
    });
    yield put(actionCreators.getActiveClientsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getActiveClientsError(e.response));
  }
}
export function* onGetAllActiveClientsInformation({ payload }) {
  const params = {
    unpaged: payload.unpaged,
  };

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/client-admin/client/clients-information`,
      {
        params,
      },
    );
    yield put(recordingActionCreator.GetClientInformationSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetClientInformationError(e.response));
  }
}

export function* onGetReportLogData({
  payload,
}: {
  clientId: number,
  employeeId: number,
  scheduledReportId: number,
}) {
  const { clientId, employeeId, scheduledReportId } = payload;
  if (!clientId || !employeeId || !scheduledReportId) throw Error("Invalid payload provided");

  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${clientId}/employee/${employeeId}/scheduled-reports/${scheduledReportId}/stats`,
    );
    yield put(actionCreators.GetReportLogDataSuccess(response.data));
    yield put(actionCreators.ToggleLogDataModal());
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetReportLogDataError(e.response));
  }
}

export function* onSearchEmployeeByName({ payload }) {
  const filters = yield select(getEmployeeNameSearchform);
  const sort = yield select(getEmployeesSort);
  const additionalFilters = payload.filters || {};
  const page = payload.page || 1;
  const sortstr = sort ? `?sort=${sortToString(sort)}` : ""; // The only way to use double criteria sorting
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee${sortstr}`,
      {
        params: {
          search: transformFilters(Object.assign({}, filters, additionalFilters), {
            last_name: OPERATORS.LIKE,
            email: OPERATORS.LIKE,
          }),
          page,
          limit: config.resultsPerPage,
        },
      },
    );
    if (payload.filters && payload.filters.id) {
      const currentEmployee = response.data.content.find(
        employee => employee.id === payload.filters.id,
      );
      yield put(actionCreators.getCurrentEmployeeSuccess(currentEmployee));
    }

    yield put(actionCreators.getEmployeesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getEmployeesError(e.response));
  }
}

export function* onGetEmployees({ payload }) {
  const filters = yield payload.filters || select(getEmployeeFilters);
  const clientId = payload.clientId;
  const sort = yield select(getEmployeesSort);
  const additionalFilters = payload.filters ? { ...filters, ...payload.filters } : {};
  const page = payload.page || 1;
  const sortstr = sort ? `?sort=${sortToString(sort)}` : ""; // The only way to use double criteria sorting

  delete filters.filters;
  const filters2 = {
    ...filters,
    ...additionalFilters,
  };

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee${sortstr}`,
      {
        params: {
          search: transformFilters(filters2, {
            id: OPERATORS.EQUALS,
            last_name: OPERATORS.LIKE,
            email: OPERATORS.LIKE,
            organisational_unit: OPERATORS.LIKE,
          }),
          page,
          limit: config.resultsPerPage,
        },
      },
    );
    if (payload.filters && payload.filters.id) {
      const currentEmployee = response.data.content.find(
        employee => employee.id === payload.filters.id,
      );
      yield put(actionCreators.getCurrentEmployeeSuccess(currentEmployee));
    }

    yield put(actionCreators.getEmployeesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getEmployeesError(e.response));
  }
}

export function* onGetEmployeesSecurityQuestions({ payload }) {
  try {
    if (!payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-employee-identity`,
    );
    yield put(
      actionCreators.getEmployeesSecurityQuestionsSuccess(response.data.securityQuestionsEnabled),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getEmployeesSecurityQuestionsError(e.response));
  }
}

export function* onExportEmployeeDeclinedReferralHistory({ payload }) {
  const { clientId, employeeId } = payload;

  if (!clientId || !employeeId) {
    throw Error("Invalid payload provided");
  }
  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${clientId}/employee/${employeeId}/referrals/export/declined`,
    );

    yield put(actionCreators.ExportEmployeeDeclinedReferralHistorySuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.ExportEmployeeDeclinedReferralHistoryError(e.response));
  }
}

export function* onExportEmployeeDeclinedReferralHistorySuccess({ payload }) {
  yield downloadCsvFile(payload, "WellbeingDeclinedReferrals.csv");
}

export function* onSendGSAppInvitation({ payload }) {
  try {
    if (!payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }
    const { employeeId, clientId } = payload;

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${clientId}/service-config/registration/send-email`,
      {
        employee_id: employeeId,
      },
    );
    yield all([
      put(actionCreators.SendGSAppInvitationSuccess(response.data.securityQuestionsEnabled)),
      put(sendSuccessMessage("INVITATION_SENT_SUCCESSFULLY")),
    ]);
  } catch (e) {
    yield put(actionCreators.SendGSAppInvitationError(e.response));
  }
}

export function* EmployeeSaga() {
  yield all([
    // Personal Details
    takeEvery(GetEmployee, onGetEmployee),
    takeEvery(ExportEmployeeReferralHistory, onExportEmployeeReferralHistory),
    takeEvery(ExportEmployeeReferralHistorySuccess, onExportEmployeeReferralHistorySuccess),
    takeEvery(ExportEmployeeDeclinedReferralHistory, onExportEmployeeDeclinedReferralHistory),
    takeEvery(
      ExportEmployeeDeclinedReferralHistorySuccess,
      onExportEmployeeDeclinedReferralHistorySuccess,
    ),
    takeEvery(GetEmployeeReferralHistory, onGetEmployeeReferralHistory),
    takeEvery(GetEmployeeActionSummary, onGetEmployeeActionSummary),
    takeLatest(GetEmployeeSchema, onGetEmployeeSchema),
    spawn(personalDetailsSaga),

    // Employment Details
    spawn(employmentDetailsSaga),

    // Medical Details
    spawn(medicalDetailsSaga),

    // User Account
    spawn(userAccountSaga),

    // Employee Search
    takeEvery(GetActiveClients, onGetActiveClients),
    takeEvery(GetAllActiveClientsInformation, onGetAllActiveClientsInformation),
    takeEvery(SearchEmployeeByName, onSearchEmployeeByName),
    takeEvery(GetEmployees, onGetEmployees),
    takeEvery(SetEmployeesSort, onGetEmployees),
    takeEvery(GetEmployeesSecurityQuestions, onGetEmployeesSecurityQuestions),
    takeEvery(GetAddEmployeeSchema, onGetAddEmployeeSchema),
    takeEvery(GetEmployeeTempSchema, onGetEmployeeTempSchema),
    takeEvery(GetReportLogData, onGetReportLogData),
    takeEvery(SendGSAppInvitation, onSendGSAppInvitation),
  ]);
}

export default EmployeeSaga;
